import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QuoteForm } from "./QuoteForm";
import { Button } from "@/components/ui/button";
import { SubmissionSuccess } from "@/components/forms/SubmissionSuccess";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { buildSubmissionContext, SUBMISSION_SOURCES } from "@/lib/submission-context";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialPromoCode?: string;
}

export function QuoteRequestDialog({ open, onOpenChange, initialPromoCode }: Props) {
  const { submitQuote, closeDrawer } = useCart();
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ reference: string | null } | null>(null);

  // Reset on close so reopening lands on the form, not the previous
  // confirmation. Mirrors QuoteDrawer.
  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) setDone(null);
  };

  const dismiss = () => {
    handleOpenChange(false);
    closeDrawer();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{done ? t("quote.success.title") : t("quote.title")}</DialogTitle>
          <DialogDescription>
            {done ? t("quote.success.body") : t("quote.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          // This dialog used to close itself on success and rely on a toast, so
          // the reference never had anywhere to appear and the confirmation
          // vanished with the dialog. It now stays open until dismissed
          // (VARS-056). Title and body live in the DialogHeader above — passing
          // them again here would have a screen reader read both.
          <SubmissionSuccess reference={done.reference} className="py-6">
            <Button variant="outline" className="rounded-xl font-bold" onClick={dismiss}>
              {t("quote.drawer.close")}
            </Button>
          </SubmissionSuccess>
        ) : (
          <QuoteForm
            busy={busy}
            formSource={SUBMISSION_SOURCES.cart}
            initialPromoCode={initialPromoCode}
            onSubmit={async (data) => {
              setBusy(true);
              // Without the catch, a rejected submit left the dialog stuck on
              // "Submitting…" with no error. Mirrors QuoteDrawer.
              try {
                const reference = await submitQuote(
                  data,
                  buildSubmissionContext(SUBMISSION_SOURCES.cart, { locale: lang }),
                );
                setDone({ reference });
              } catch (err) {
                toast.error(err instanceof Error ? err.message : t("quote.drawer.error"));
              } finally {
                setBusy(false);
              }
            }}
            Footer={({ children }) => <DialogFooter>{children}</DialogFooter>}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
